// --- SCOPE CONSTANTS & MAPS ---
const DEVICE_CACHE = getDeviceName();
const BROWSER_CACHE = getBrowserName();
const LOCATION_HOSTNAME = location.hostname;
const COUNTRY_CODE = getCountryCode();
const IGNORED_SKUS = new Set(['PACKA']);

const graphqlEndpoint = `https://${window.shopDomain}/api/2025-10/graphql.json`;
const storefrontAccessToken = window.themeSettings?.custom_app_token;
const localCurrency = window.localCurrency || '';
const conversionNeeded = localCurrency !== 'EUR';
const translationNeeded = window.language !== 'en';

const GENDER_MAP = {
  40093: 'Unisex',
  40196: 'Men',
  40188: 'Women',
  40186: 'Kids',
};

const DESIGNS_MAP = {
  178846: 'White',
  40152: 'Burgundy',
  40172: 'Beige',
  40149: 'Purple',
  40153: 'Brown',
  178852: 'Blue',
  278697: 'Ochre',
  40151: 'Orange',
  93489: 'Colorful',
  40155: 'Pink',
  278699: 'Light Blue',
  278698: 'Light Pink',
  278700: 'Light Green',
  278696: 'Turquoise',
  251335: 'Multicolored',
  40150: 'Green',
  40156: 'Red',
  40059: 'Black',
  40173: 'Grey',
  40154: 'Yellow',
  249164: 'Basic',
  249162: 'Hobby and Sport',
  249161: 'Food',
  246328: 'Nature',
  249163: 'Holidays',
  249160: 'All',
  246302: 'Animals',
  259175: 'Flip-flops',
  259174: 'Sandals',
};

const LOCATION_TYPE_MAP = {
  index: 'home',
  collection: 'list',
  product: 'detail',
  cart: 'cart',
};

// --- UTILITIES ---
const isTrackingDisabled = () => typeof isExponeaDisabled !== 'undefined' && isExponeaDisabled;

const getDomSpecs = (id) => {
  const element = document.getElementById(id);
  if (!element) return null;
  try {
    return JSON.parse(element.textContent);
  } catch (e) {
    console.warn('Exponea Tracking: Failed to parse JSON from #' + id, e);
    return null;
  }
};

const normalizePrice = (price) => {
  if (price == null) return 0;
  return parseFloat(String(price).replace(',', '.')) || 0;
};

const getUrlPath = (urlString) => {
  if (!urlString) return '';
  try {
    return new URL(urlString, window.location.origin).pathname;
  } catch (e) {
    return String(urlString).split('?')[0];
  }
};

const waitForDependencies = (checkFn, maxRetries = 25, interval = 200) => {
  return new Promise((resolve, reject) => {
    let retries = 0;
    const check = () => {
      if (checkFn()) return resolve();
      if (++retries >= maxRetries) return reject(new Error('Dependencies timeout'));
      setTimeout(check, interval);
    };
    check();
  });
};

function getBaseContext() {
  return {
    os: navigator.platform,
    device: DEVICE_CACHE,
    browser: BROWSER_CACHE,
    location: location.href,
    location_hostname: LOCATION_HOSTNAME,
    country_code: COUNTRY_CODE,
    local_currency: localCurrency,
  };
}

function normalizeCategoryPath(path = []) {
  return {
    category_level_1: path[0] || '',
    category_level_2: path[1] || '',
    category_level_3: path[2] || '',
    category_level_4: path[3] || '',
    category_level_5: path[4] || '',
    category_path: path.join(' > '),
  };
}

function getDeviceName() {
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Windows Phone/i.test(ua)) return 'Windows Phone';
  if (/BlackBerry/i.test(ua)) return 'BlackBerry';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Unknown';
}

function getBrowserName() {
  const ua = navigator.userAgent;
  if (/Chrome/i.test(ua) && !/Chromium/i.test(ua) && !/Edg/i.test(ua)) return 'Chrome';
  if (/Firefox/i.test(ua)) return 'Firefox';
  if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return 'Safari';
  if (/Opera|OPR/i.test(ua)) return 'Opera';
  if (/Edge|Edg/i.test(ua)) return 'Edge';
  if (/MSIE|Trident/i.test(ua)) return 'Internet Explorer';
  return 'Unknown';
}

function getCountryCode() {
  const tld = location.hostname.split('.').pop() || '';
  return { com: 'en' }[tld] || tld;
}

function fetchCurrencyEur(price_lc) {
  const eurToLocalRate = window.eurToLocalRate;
  if (!eurToLocalRate || eurToLocalRate <= 0) {
    console.warn('Invalid EUR to local rate found in metafield:', eurToLocalRate);
    return null;
  }
  return parseFloat((price_lc / eurToLocalRate).toFixed(2));
}

function getGender(genderCode) {
  return GENDER_MAP[genderCode] || null;
}
function getDesign(designCode) {
  return DESIGNS_MAP[designCode] || null;
}
function getLocationType() {
  return LOCATION_TYPE_MAP[window.pageType] || 'other';
}

async function getItemProductSpecs(url) {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching product specs:', error);
    return null;
  }
}

async function getCartData() {
  try {
    const response = await fetch(window.Shopify.routes.root + 'cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const responseJson = await response.json();
    return responseJson;
  } catch (error) {
    console.error('Error fetching cart data:', error);
    return null;
  }
}

// --- CART EVENT HANDLING ---
async function initializeCartListener() {
  if (isTrackingDisabled()) return;

  try {
    await waitForDependencies(() => typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined');

    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      const productSku =
        event.cartData?.sku || event.cartData?.items_added?.[0]?.sku || event.cartData?.items_removed?.[0]?.sku;
      if (productSku && IGNORED_SKUS.has(productSku)) return;

      const mappedData = await mapCartData(event);
      if (!mappedData) return;

      if (typeof exponeaTrackSafe === 'function') exponeaTrackSafe('cart_update', mappedData);

      if (typeof exponea !== 'undefined') {
        if (window.exponeaCustomer && mappedData.cart_token) {
          exponea.identify({ cart_id: mappedData.cart_token });
        } else if (mappedData.cart_token) {
          exponea.start({ customer: { cart_id: mappedData.cart_token } });
        }
      }

      addExponeaUserCookie(event?.attributes);
    });
  } catch (e) {
    console.warn('Cart listener initialization timed out:', e);
  }
}

function addExponeaUserCookie(eventAttributes) {
  const match = document.cookie.match(/(^|;)\s*__exponea_etc__=([^;]+)/);
  const exponeaCookieValue = match ? match[2] : null;

  if (eventAttributes && eventAttributes.exponeaCookie === exponeaCookieValue) return;

  if (exponeaCookieValue) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { exponeaCookie: exponeaCookieValue } }),
    }).catch(console.error);
  }
}

function getTrackingContext(event) {
  const urlParams = new URLSearchParams(window.location.search);
  const urlSource = urlParams.get('source');

  let source = 'natural';
  let addType = '';
  let position = null;

  const element = event.element;

  if (element) {
    const isPdpProductAdded = Boolean(element.closest('product-info'));

    // Navigation links inside product-info should not inherit indirect source attribution on click
    if (element.tagName === 'A' && isPdpProductAdded) {
      return { source: 'natural', addType: '', position: null };
    }

    // 1. Check for Indirect Additions (from PDP landed via Search/Recommendation/Category link)
    if (isPdpProductAdded) {
      position = parseInt(urlParams.get('list_position'), 10) || null;
      if (['recommendation', 'search', 'category'].includes(urlSource)) {
        source = urlSource;
        addType = 'indirect';
      }
    } else {
      const isRecoCardAdded = Boolean(element.closest('exponea-reco'));
      const isSearchCardAdded = Boolean(
        element.closest('.template-search__results') ||
        element.closest('predictive-search') ||
        element.closest('constructor-autocomplete'),
      );
      const isCollectionCardAdded = Boolean(element.closest('#product-grid') && window.pageType === 'collection');

      // Calculate list position of the product card
      const card = element.closest('.js-product-card-wrapper');
      if (card) {
        const grid =
          card.closest('#product-grid') || card.closest('.template-search__results') || card.closest('.grid');
        if (grid) {
          const cards = Array.from(grid.querySelectorAll('.js-product-card-wrapper'));
          const idx = cards.indexOf(card) + 1;
          if (idx > 0) position = idx;
        }
      }
      // 2. Check for Direct Additions (from Result Cards / Grids)
      if (isRecoCardAdded) {
        source = 'recommendation';
        addType = 'direct';
      } else if (isSearchCardAdded) {
        source = 'search';
        addType = 'direct';
      } else if (isCollectionCardAdded) {
        source = 'category';
        addType = 'direct';
      }
    }
  }
  return { source, addType, position };
}
async function mapCartData(event) {
  if (event.source === 'cart-items') {
    const itemsRemoved = event.cartData?.items_removed || [];
    const itemsAdded = event.cartData?.items_added || [];
    const eventAction = itemsRemoved.length > 0 ? 'remove' : 'add';
    const itemsUpdated = itemsRemoved.length > 0 ? itemsRemoved : itemsAdded;
    const cartItems = event.cartData?.items || [];

    if (!itemsUpdated.length) return null;

    const itemProductUrl = getUrlPath(itemsUpdated[0]?.url || event.cartData?.url);
    const itemProductSpecs = (await getItemProductSpecs(itemProductUrl + '?view=specs')) || {};

    const itemName = itemsUpdated[0].untranslated_product_title;
    const itemPriceLc = normalizePrice(itemsUpdated[0].presentment_price);
    const itemPrice = conversionNeeded ? fetchCurrencyEur(itemPriceLc) : itemPriceLc;

    const totalPriceLc = parseFloat((normalizePrice(event?.cartData?.items_subtotal_price) / 100).toFixed(2));
    const totalPrice = conversionNeeded ? fetchCurrencyEur(totalPriceLc) : totalPriceLc;

    return {
      ...getBaseContext(),
      ...normalizeCategoryPath(itemProductSpecs.category_path),
      action: eventAction || '',
      item_brand: itemsUpdated[0].vendor || '',
      cart_token: String(event?.cartData?.token || '').split('?')[0],
      item_gender: getGender(itemProductSpecs.gender) || '',
      item_id: itemsUpdated[0].sku || '',
      item_group_id: itemProductSpecs.id || '',
      item_name: itemName || '',
      item_price_lc: itemPriceLc,
      item_price: itemPrice,
      item_tags: itemProductSpecs.tags || [],
      item_color: getDesign(itemProductSpecs.color) || '',
      item_design: getDesign(itemProductSpecs.design) || '',
      item_size: itemsUpdated[0].variant_title || '',
      items: cartItems.map((item) => ({ item_id: item.sku, quantity: item.quantity })),
      item_quantity: itemsUpdated[0].quantity,
      total_price_lc: totalPriceLc,
      total_price: totalPrice,
      total_quantity: event?.cartData?.item_count,
    };
  }

  if (event.source === 'product-form' || event.source === 'cart-items-add') {
    const eventType = 'add';
    const itemKey = event.cartData?.key;
    if (!itemKey) return null;

    const currentCartData = await getCartData();
    if (!currentCartData || !currentCartData.items) return null;

    const item = currentCartData.items.find((i) => i.key === itemKey);
    if (!item) return null;

    const itemUrl = getUrlPath(item.url);
    const itemProductSpecs = (await getItemProductSpecs(itemUrl + '?view=specs')) || {};
    const itemQuantity = 1;

    const itemName = item.product_title || item.product?.title || item.title;
    const itemPriceLc = normalizePrice(item.presentment_price);
    const itemPrice = conversionNeeded ? fetchCurrencyEur(itemPriceLc) : itemPriceLc;

    const totalPriceLc = parseFloat((normalizePrice(currentCartData.total_price) / 100).toFixed(2));
    const totalPrice = conversionNeeded ? fetchCurrencyEur(totalPriceLc) : totalPriceLc;
    const { source, addType, position } = getTrackingContext(event);

    return {
      ...getBaseContext(),
      ...normalizeCategoryPath(itemProductSpecs.category_path),
      action: eventType,
      item_brand: item.vendor || '',
      cart_token: String(currentCartData?.token || '').split('?')[0],
      item_gender: getGender(itemProductSpecs.gender) || '',
      item_id: item.sku || '',
      item_group_id: itemProductSpecs.id || '',
      item_name: itemName,
      item_price_lc: itemPriceLc,
      item_price: itemPrice,
      item_tags: itemProductSpecs.tags || [],
      item_color: getDesign(itemProductSpecs.color) || '',
      item_design: getDesign(itemProductSpecs.design) || '',
      item_size: item.variant_title || '',
      items: currentCartData.items.map((i) => ({ item_id: i.sku, quantity: i.quantity })),
      item_quantity: itemQuantity,
      total_price_lc: totalPriceLc,
      total_price: totalPrice,
      total_quantity: currentCartData.item_count,
      source: source,
      add_type: addType,
      position: position,
    };
  }
}

// --- LOGIN TRACKING ---
async function initializeLoginTracking() {
  if (isTrackingDisabled()) return;

  try {
    await waitForDependencies(() => typeof window.exponea?.track === 'function', 25, 500);

    const { email_id: email, shopify_id: id } = window.exponeaCustomer || {};
    const loginFlagKey = 'exponea_login_tracked';
    const customerIdKey = 'exponea_last_tracked_customer_key';

    // One-time migration from legacy key
    const legacyEmailKey = 'exponea_last_tracked_email';
    if (localStorage.getItem(legacyEmailKey) && localStorage.getItem(legacyEmailKey) === email) {
      localStorage.setItem(loginFlagKey, 'true');
      localStorage.setItem(customerIdKey, String(id));
      localStorage.removeItem(legacyEmailKey);
    }

    const lastTrackedId = localStorage.getItem(customerIdKey);
    const hasTrackedBefore = localStorage.getItem(loginFlagKey) === 'true';

    // Explicit ID check for cross-account switching on same device
    const isNewSession = !hasTrackedBefore || (id && String(id) !== lastTrackedId);

    if (email) {
      if (isNewSession && typeof exponeaTrackSafe === 'function') {
        //console.log('Exponea Auth Tracking:', email);
        exponeaTrackSafe('login', {
          location_hostname: LOCATION_HOSTNAME,
          country_code: COUNTRY_CODE,
          email,
        });

        localStorage.setItem(loginFlagKey, 'true');
        if (id) localStorage.setItem(customerIdKey, String(id));
      } else {
        //console.log('Exponea Auth Tracking: Already tracked');
      }
    } else if (hasTrackedBefore) {
      //console.log('Exponea Auth Tracking: Logout');
      // Cleanup on logout/missing session
      localStorage.removeItem(loginFlagKey);
      localStorage.removeItem(customerIdKey);
    } else {
      //console.log('Exponea Auth Tracking: No email');
    }
  } catch (error) {
    console.warn('Exponea Auth Tracking Failed:', error);
  }
}

// --- VARIANT/SIZE TRACKING ---
async function initializeSelectedSizeListener() {
  if (isTrackingDisabled()) return;

  try {
    await waitForDependencies(() => typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined');

    subscribe(PUB_SUB_EVENTS.variantChange, async (event) => {
      const mappedData = await mapData(event);
      if (mappedData && event.source === 'variant:changed' && typeof exponeaTrackSafe === 'function') {
        setTimeout(() => {
          exponeaTrackSafe('selected_size', mappedData);
        }, 0);
      }
    });
  } catch (error) {
    console.warn('Selected size listener initialization timed out:', error);
  }
}

document.addEventListener('variant:changed', (event) => {
  if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS.variantChange, {
      source: 'variant:changed',
      variantData: event.detail.currentVariant,
    });
  }
});

async function mapData(event) {
  if (event.source === 'variant:changed') {
    const productSpecsJson = getDomSpecs('product-specs');
    if (!productSpecsJson) return null;

    if (!event.variantData) {
      console.warn('No variant data found in event:', event);
      return null;
    }

    const itemPriceFormatted = normalizePrice(event.variantData.price) / 100;
    const itemPriceEur = conversionNeeded ? fetchCurrencyEur(itemPriceFormatted) : itemPriceFormatted;

    return {
      ...getBaseContext(),
      ...normalizeCategoryPath(productSpecsJson.category_path || []),
      availability: event.variantData.available,
      item_brand: productSpecsJson.brand || '',
      item_gender: getGender(productSpecsJson.gender) || '',
      item_id: productSpecsJson.id || '',
      item_name: productSpecsJson.name,
      item_price_lc: itemPriceFormatted,
      item_price: itemPriceEur,
      item_size: event.variantData.option1 || '',
      item_tags: productSpecsJson.tags || [],
      item_color: getDesign(productSpecsJson.color) || '',
      item_design: getDesign(productSpecsJson.design) || '',
      discount_percentage_displayed: productSpecsJson.displayed_percentage_discount || 0,
    };
  }
}

// --- WISHLIST TRACKING ---
async function trackWishlistUpdate(wishlist, action, btn = null, item) {
  if (typeof exponeaTrackSafe !== 'function') return;

  try {
    const itemPriceLc = normalizePrice(item.price);
    const itemPrice = conversionNeeded ? fetchCurrencyEur(itemPriceLc) : itemPriceLc;

    const comparePrice = normalizePrice(item.compare_at_price);
    let discountPercentageDisplayed = 0;

    if (comparePrice > itemPriceLc) {
      discountPercentageDisplayed = Math.round(((comparePrice - itemPriceLc) * 100) / comparePrice);
    }

    let source = 'natural';
    if (btn instanceof Element) {
      const trackingContext = getTrackingContext({ element: btn });
      source = trackingContext.source;
    }

    const eventData = {
      ...getBaseContext(),
      ...normalizeCategoryPath(item.category_path),
      action: action,
      availability: item.available,
      item_brand: item.vendor || '',
      item_gender: getGender(item.gender) || '',
      item_id: item.sku || '',
      item_name: item.title || '',
      item_price_lc: itemPriceLc,
      item_price: itemPrice,
      item_tags: item.tags || [],
      item_color: getDesign(item.color) || '',
      item_design: getDesign(item.design) || '',
      items: wishlist.map((wishlistItem) => ({ item_id: wishlistItem.sku })),
      wishlist_item_count: wishlist.length,
      discount_percentage_displayed: discountPercentageDisplayed,
      source: source,
    };

    exponeaTrackSafe('wishlist_update', eventData);
  } catch (error) {
    console.error('Error in wishlist tracking:', error);
  }
}

window.addEventListener('wishlist:updated', async ({ detail }) => {
  if (isTrackingDisabled()) return;

  const { wishlist, action, handle, btn } = detail;
  if (!handle || !action) return;

  try {
    // Try to get specs from DOM if we are on the PDP of the same product
    let productSpecsJson = getDomSpecs('product-specs');
    if (productSpecsJson && productSpecsJson.handle !== handle) {
      productSpecsJson = null;
    }

    if (!productSpecsJson) {
      const root = window.routes?.root_url || '/';
      const cleanRoot = root.endsWith('/') ? root : root + '/';
      productSpecsJson = await getItemProductSpecs(`${cleanRoot}products/${handle}?view=specs`);
    }

    if (!productSpecsJson) {
      console.warn('Exponea Tracking: Could not retrieve product specs for', handle);
      return;
    }

    const productData = {
      available: productSpecsJson.available,
      title: productSpecsJson.name,
      compare_at_price: productSpecsJson.compare_at_price,
      price: productSpecsJson.price,
      vendor: productSpecsJson.brand,
      tags: productSpecsJson.tags,
      sku: productSpecsJson.id,
      gender: productSpecsJson.gender,
      color: productSpecsJson.color,
      design: productSpecsJson.design,
      category_path: productSpecsJson.category_path,
    };

    await trackWishlistUpdate(wishlist, action, btn, productData);
  } catch (error) {
    console.error('Error fetching product data for wishlist tracking:', error);
  }
});

// --- ITEM VIEW TRACKING ---
async function initializeItemListener() {
  if (isTrackingDisabled()) return;

  try {
    const productSpecsJson = getDomSpecs('product-specs');
    if (!productSpecsJson) return;

    const itemPriceFormatted = normalizePrice(productSpecsJson.price);
    const itemPriceEur = conversionNeeded ? fetchCurrencyEur(itemPriceFormatted) : itemPriceFormatted;

    const urlParams = new URLSearchParams(window.location.search);
    const rawSource = urlParams.get('source');
    const source = ['recommendation', 'search', 'category'].includes(rawSource) ? rawSource : 'natural';
    const listPosition = parseInt(urlParams.get('list_position'), 10) || null;

    const mappedData = {
      ...getBaseContext(),
      ...normalizeCategoryPath(productSpecsJson.category_path),
      item_id: productSpecsJson.id || '',
      item_name: productSpecsJson.name || '',
      item_brand: productSpecsJson.brand || '',
      item_price_lc: itemPriceFormatted,
      item_price: itemPriceEur,
      item_tags: productSpecsJson.tags || [],
      item_gender: getGender(productSpecsJson.gender) || '',
      item_color: getDesign(productSpecsJson.color),
      item_design: getDesign(productSpecsJson.design),
      available: productSpecsJson.available,
      displayed_percentage_discount: productSpecsJson.displayed_percentage_discount || 0,
      source: source,
      position: listPosition,
    };

    if (typeof exponeaTrackSafe === 'function') exponeaTrackSafe('view_item', mappedData);
  } catch (error) {
    console.error('Error in item tracking:', error);
  }
}

// --- CATEGORY VIEW TRACKING ---
async function fetchCollectionTitles(handles) {
  if (!handles || handles.length === 0) return {};

  let queryFields = '';
  let variables = {};

  handles.forEach((handle, i) => {
    if (handle) {
      queryFields += `collection${i}: collection(handle: $handle${i}) { title }\n`;
      variables[`handle${i}`] = handle;
    }
  });

  if (!queryFields) return {};

  const query = `
    query GetCollectionTitles(${Object.keys(variables)
      .map((k) => `$${k}: String!`)
      .join(', ')}) {
      ${queryFields}
    }`;

  try {
    const response = await fetch(graphqlEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
      },
      body: JSON.stringify({ query, variables }),
    });

    const data = await response.json();
    if (data.errors) return null;

    const titles = {};
    if (data.data) {
      handles.forEach((handle, i) => {
        if (handle) {
          const alias = `collection${i}`;
          titles[handle] = data.data[alias] ? data.data[alias].title : handle;
        }
      });
    }
    return titles;
  } catch (error) {
    console.error('Network error fetching collection titles:', error);
    return null;
  }
}

async function initializeCategoryListener() {
  if (isTrackingDisabled()) return;

  try {
    await waitForDependencies(() => typeof window.exponea?.track === 'function', 25, 500);

    const collectionSpecsJson = getDomSpecs('collection-specs');
    if (!collectionSpecsJson) return;
    let categoryPathHandles = collectionSpecsJson.category_path_handles || [];

    const engine = document.querySelector('constructor-browse') ? 'constructor' : 'shopify';

    const mappedData = {
      ...getBaseContext(),
      ...normalizeCategoryPath(categoryPathHandles),
      engine: engine,
    };

    if (typeof exponeaTrackSafe === 'function') exponeaTrackSafe('view_category', mappedData);
  } catch (error) {
    console.error('Error in category tracking:', error);
  }
}

// --- SEARCH TRACKING ---
/**
 * MIN_CHARACTERS: Minimum query length before triggering a tracking event
 */
const SEARCH_TRACKING_CONSTANTS = {
  MIN_CHARACTERS: 2,
};

/**
 * Centralized Search Tracking Listener
 * Subscribes to searchQuery events from any search engine (Shopify or Constructor)
 * and fires the Exponea 'search' event with standardized metadata.
 */
function initializeSearchTracking() {
  if (isTrackingDisabled()) return;

  const onReady = () => {
    subscribe(PUB_SUB_EVENTS.searchQuery, (data) => {
      const {
        query,
        search_context,
        engine,
        products_count = 0,
        collections_count = 0,
        result_product_ids = [],
      } = data;

      if (!query || query.length < SEARCH_TRACKING_CONSTANTS.MIN_CHARACTERS) return;

      const executeTracking = () => {
        const eventData = {
          ...getBaseContext(),
          query: query,
          search_context: search_context,
          engine: engine,
          products_count: products_count,
          collections_count: collections_count,
          result_product_ids: result_product_ids.slice(0, 6),
        };

        if (typeof exponeaTrackSafe === 'function') {
          exponeaTrackSafe('search', eventData);
        }
      };

      // setTimeout to ensure tracking doesn't block the main thread (INP optimization)
      setTimeout(executeTracking, 0);
    });
  };

  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    onReady();
  } else {
    waitForDependencies(() => typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined')
      .then(onReady)
      .catch((error) => console.warn('Search tracking listener initialization timed out:', error));
  }
}

/**
 * Global Click Hook for Search and Category Attribution
 * Intercepts clicks on search results to append '?source=search' and category products to append '?source=category'.
 */
document.addEventListener(
  'click',
  (event) => {
    if (isTrackingDisabled()) return;

    const link = event.target.closest('a[href]');
    const cta = event.target.closest('button[type="submit"]');

    if (!link || cta) return;

    try {
      const url = new URL(link.href, window.location.origin);
      const isProductPath = /\/(products)\//.test(url.pathname);
      if (!isProductPath) return;

      const { source, position } = getTrackingContext({ element: link });

      if (source && source !== 'natural') {
        // Append tracking source query parameter and list position
        if (url.origin === window.location.origin) {
          url.searchParams.set('source', source);
          if (position) {
            url.searchParams.set('list_position', position);
          }
          link.href = url.pathname + url.search + url.hash;
        }
      }
    } catch (e) {
      // Ignore URL parsing errors
    }
  },
  { capture: true },
);

// --- INITIALIZATION ---
document.addEventListener('DOMContentLoaded', () => {
  initializeCartListener();
  initializeLoginTracking();
  initializeSelectedSizeListener();
  initializeItemListener();
  initializeCategoryListener();
  initializeSearchTracking();
});
